package com.havn.app.core.designsystem

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.ScrollState
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxScope
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyListState
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.pulltorefresh.PullToRefreshBox
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.snapshotFlow
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import com.havn.app.core.localization.LocalHavnStrings
import com.havn.app.ui.theme.LocalHavnThemeTokens
import kotlinx.coroutines.flow.distinctUntilChanged

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun HavnPullToRefreshBox(
    isRefreshing: Boolean,
    onRefresh: () -> Unit,
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
    content: @Composable BoxScope.() -> Unit
) {
    if (havnIsWebShell || !enabled) {
        Box(modifier = modifier, content = content)
    } else {
        PullToRefreshBox(
            isRefreshing = isRefreshing,
            onRefresh = onRefresh,
            modifier = modifier,
            indicator = {
                HavnRefreshOverlayIndicator(
                    isRefreshing = isRefreshing,
                    modifier = Modifier
                        .align(Alignment.TopCenter)
                        .padding(top = 10.dp)
                )
            },
            content = content
        )
    }
}

@Composable
fun HavnRefreshOverlayIndicator(
    isRefreshing: Boolean,
    modifier: Modifier = Modifier
) {
    if (!isRefreshing) return
    val tokens = LocalHavnThemeTokens.current
    Surface(
        modifier = modifier.size(36.dp),
        shape = MaterialTheme.shapes.large,
        color = tokens.surface.copy(alpha = 0.92f),
        border = BorderStroke(1.dp, tokens.border.copy(alpha = 0.72f))
    ) {
        Box(contentAlignment = Alignment.Center) {
            CircularProgressIndicator(
                modifier = Modifier.size(18.dp),
                color = tokens.primaryAccent,
                strokeWidth = 2.dp
            )
        }
    }
}

@Composable
fun HavnWebRefreshAction(
    onRefresh: () -> Unit,
    modifier: Modifier = Modifier,
    isRefreshing: Boolean = false
) {
    if (!havnIsWebShell) return
    val tokens = LocalHavnThemeTokens.current
    Surface(
        modifier = modifier.size(42.dp),
        shape = MaterialTheme.shapes.large,
        color = tokens.surface.copy(alpha = 0.92f),
        border = BorderStroke(1.dp, tokens.border.copy(alpha = 0.78f))
    ) {
        Box(
            modifier = Modifier.clickable(enabled = !isRefreshing, onClick = onRefresh),
            contentAlignment = Alignment.Center
        ) {
            if (isRefreshing) {
                CircularProgressIndicator(
                    modifier = Modifier.size(18.dp),
                    color = tokens.primaryAccent,
                    strokeWidth = 2.dp
                )
            } else {
                HavnIcon(
                    icon = HavnIconKey.Refresh,
                    contentDescription = "Refresh",
                    tint = tokens.primaryAccent,
                    modifier = Modifier.size(21.dp)
                )
            }
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun HavnScreenFrame(
    eyebrow: String,
    title: String,
    subtitle: String,
    metrics: List<HavnMetric> = emptyList(),
    backLabel: String? = null,
    onBack: (() -> Unit)? = null,
    isRefreshing: Boolean = false,
    onRefresh: (() -> Unit)? = null,
    compactHeader: Boolean = false,
    scrollToTopSignal: Int = 0,
    onScrolledNearBottom: (() -> Unit)? = null,
    headerTrailing: (@Composable () -> Unit)? = null,
    content: @Composable () -> Unit
) {
    val strings = LocalHavnStrings.current
    val tokens = LocalHavnThemeTokens.current
    val scrollState = rememberScrollState()
    val screenPadding = PaddingValues(
        start = 22.dp,
        top = 24.dp,
        end = 22.dp,
        bottom = if (havnNeedsNativeTabBarContentInset) 116.dp else 24.dp
    )
    var gestureStartedAtTop by remember { mutableStateOf(false) }
    HavnInfiniteScrollLoadEffect(
        scrollState = scrollState,
        enabled = onScrolledNearBottom != null,
        isLoading = isRefreshing,
        onLoadMore = { onScrolledNearBottom?.invoke() }
    )
    var handledScrollToTopSignal by remember { mutableStateOf(scrollToTopSignal) }
    LaunchedEffect(scrollToTopSignal) {
        if (scrollToTopSignal > handledScrollToTopSignal) {
            if (scrollState.value == 0) {
                onRefresh?.invoke()
            } else {
                scrollState.animateScrollTo(0)
            }
        }
        handledScrollToTopSignal = scrollToTopSignal
    }
    HavnPullToRefreshBox(
        isRefreshing = isRefreshing,
        onRefresh = {
            if (gestureStartedAtTop) {
                onRefresh?.invoke()
            }
        },
        enabled = onRefresh != null,
        modifier = Modifier
            .fillMaxSize()
            .havnRefreshStartGate(scrollState) { gestureStartedAtTop = it }
    ) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .background(
                    Brush.verticalGradient(
                        colors = listOf(tokens.gradientStart, tokens.background, tokens.gradientEnd)
                    )
                )
                .verticalScroll(scrollState)
                .padding(screenPadding)
        ) {
            if (compactHeader && onBack == null) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text(
                        text = title,
                        modifier = Modifier.weight(1f),
                        color = tokens.primaryText,
                        style = HavnTopHeaderTextStyle
                    )
                    headerTrailing?.invoke()
                }
            } else {
                if (onBack != null) {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .clickable(onClick = onBack)
                        .padding(vertical = 6.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    HavnIcon(
                        icon = HavnIconKey.Back,
                        contentDescription = backLabel ?: strings.back,
                        tint = tokens.primaryAccent
                    )
                    if (backLabel?.isNotBlank() != false) {
                        Spacer(Modifier.width(6.dp))
                        Text(
                            text = backLabel ?: strings.back,
                            modifier = Modifier.weight(1f),
                            color = tokens.primaryAccent,
                            style = MaterialTheme.typography.labelLarge,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis
                        )
                    }
                }
                Spacer(Modifier.height(12.dp))
                }
                if (eyebrow.isNotBlank()) {
                    Text(
                        text = eyebrow,
                        color = tokens.primaryAccent,
                        style = MaterialTheme.typography.labelLarge
                    )
                    Spacer(Modifier.height(8.dp))
                }
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    Text(
                        text = title,
                        modifier = Modifier.weight(1f),
                        color = tokens.primaryText,
                        style = HavnTopHeaderTextStyle,
                        maxLines = 2,
                        overflow = TextOverflow.Ellipsis
                    )
                    headerTrailing?.invoke()
                }
                if (subtitle.isNotBlank()) {
                    Spacer(Modifier.height(8.dp))
                    Text(
                        text = subtitle,
                        color = tokens.secondaryText,
                        style = MaterialTheme.typography.bodyLarge
                    )
                }
            }

            if (metrics.isNotEmpty()) {
                Spacer(Modifier.height(24.dp))
                Row(
                    horizontalArrangement = Arrangement.spacedBy(12.dp),
                    modifier = Modifier.fillMaxWidth()
                ) {
                    metrics.forEach { metric ->
                        HavnMetricCard(metric, Modifier.weight(1f))
                    }
                }
            }

            Spacer(Modifier.height(24.dp))
            content()
            Spacer(Modifier.height(16.dp))
        }
    }
}

@Composable
fun HavnInfiniteListLoadEffect(
    listState: LazyListState,
    enabled: Boolean,
    isLoading: Boolean,
    itemCount: Int,
    prefetchDistance: Int = 4,
    onLoadMore: () -> Unit
) {
    LaunchedEffect(listState, enabled, isLoading, itemCount, prefetchDistance) {
        if (!enabled || isLoading) return@LaunchedEffect
        snapshotFlow {
            val layoutInfo = listState.layoutInfo
            val totalItems = layoutInfo.totalItemsCount
            val lastVisible = layoutInfo.visibleItemsInfo.lastOrNull()?.index ?: -1
            totalItems > 0 && lastVisible >= totalItems - 1 - prefetchDistance
        }
            .distinctUntilChanged()
            .collect { nearEnd ->
                if (nearEnd && enabled && !isLoading) {
                    onLoadMore()
                }
            }
    }
}

@Composable
fun HavnInfiniteScrollLoadEffect(
    scrollState: ScrollState,
    enabled: Boolean,
    isLoading: Boolean,
    thresholdPx: Int = 360,
    onLoadMore: () -> Unit
) {
    LaunchedEffect(scrollState, enabled, isLoading, thresholdPx) {
        if (!enabled || isLoading) return@LaunchedEffect
        snapshotFlow {
            scrollState.maxValue > 0 && scrollState.value >= scrollState.maxValue - thresholdPx
        }
            .distinctUntilChanged()
            .collect { nearBottom ->
                if (nearBottom && enabled && !isLoading) {
                    onLoadMore()
                }
            }
    }
}

@Composable
fun HavnMetricCard(metric: HavnMetric, modifier: Modifier = Modifier) {
    val tokens = LocalHavnThemeTokens.current
    Card(
        modifier = modifier,
        colors = CardDefaults.cardColors(containerColor = tokens.surface),
        border = BorderStroke(1.dp, tokens.border)
    ) {
        Column(Modifier.padding(14.dp)) {
            Text(metric.label, color = tokens.secondaryText, style = MaterialTheme.typography.labelLarge)
            Spacer(Modifier.height(6.dp))
            Text(metric.value, color = tokens.primaryText, style = MaterialTheme.typography.headlineMedium)
            Spacer(Modifier.height(2.dp))
            Text(metric.delta, color = tokens.primaryAccent, style = MaterialTheme.typography.labelLarge)
        }
    }
}

@Composable
fun HavnActionPanel(title: String, body: String, action: String, modifier: Modifier = Modifier) {
    val tokens = LocalHavnThemeTokens.current
    Surface(
        modifier = modifier
            .fillMaxWidth()
            .border(1.dp, tokens.primaryAccent, MaterialTheme.shapes.large),
        color = tokens.surface,
        shape = MaterialTheme.shapes.large
    ) {
        Row(
            modifier = Modifier.padding(18.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Column(Modifier.weight(1f)) {
                Text(title, color = tokens.primaryText, style = MaterialTheme.typography.titleLarge)
                Spacer(Modifier.height(6.dp))
                Text(body, color = tokens.secondaryText, style = MaterialTheme.typography.bodyLarge)
            }
            Spacer(Modifier.width(16.dp))
            Text(action, color = tokens.primaryAccent, style = MaterialTheme.typography.labelLarge)
        }
    }
}

data class HavnMetric(
    val label: String,
    val value: String,
    val delta: String
)

private val ColorStopGraphite = HavnGraphite.copy(alpha = 0.92f)
